/**
 * ServiceTileSelector - Visual tile grid for selecting service categories
 *
 * One tile per category in the tenant's ACTIVE TRADE PACK, tap to toggle.
 *
 * This used to be a hardcoded array of the ten telco `DealCategory` members
 * with their icons and colours baked in, which meant a gate-installer tenant
 * was offered NBN and Starlink. The list now comes from
 * `useServiceCategories()`; `packIcon` / `packColor` turn the pack's icon and
 * colour STRINGS into components and Tailwind classes.
 *
 * Selection is still typed `DealCategory[]` because that's what the deal
 * schema and every downstream consumer store. Pack categories are plain
 * strings that widen that union at runtime — the cast at the toggle boundary
 * is deliberate and marked. Narrowing the schema to `string[]` is a separate
 * migration; see docs/TRADE_PACKS.md.
 *
 * No loading state by design: `useServiceCategories()` returns the static
 * telco fallback synchronously, so first paint is never empty and never
 * shifts (pack-engine invariant 5).
 */

import { motion } from 'framer-motion';
import { DealCategory } from '@/types/theo-workflow.types';
import { useServiceCategories } from '@/hooks/useTradePack';
import { packColor, packIcon } from '@/data/trade-pack-visuals';

interface Props {
  selectedServices: DealCategory[];
  onChange: (services: DealCategory[]) => void;
  disabled?: boolean;
}

export function ServiceTileSelector({ selectedServices, onChange, disabled }: Props) {
  const categories = useServiceCategories();

  const toggle = (key: string) => {
    if (disabled) return;
    // Pack categories are runtime strings that widen the DealCategory union.
    const cat = key as DealCategory;
    if (selectedServices.includes(cat)) {
      onChange(selectedServices.filter((s) => s !== cat));
    } else {
      onChange([...selectedServices, cat]);
    }
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {categories.map((category) => {
        const isSelected = selectedServices.includes(category.key as DealCategory);
        const Icon = packIcon(category.icon);
        const colors = packColor(category.color);

        return (
          <motion.button
            key={category.key}
            type="button"
            onClick={() => toggle(category.key)}
            disabled={disabled}
            aria-pressed={isSelected}
            whileTap={{ scale: 0.97 }}
            className={`
              relative p-4 rounded-xl border-2 transition-all text-left
              ${isSelected
                ? `${colors.selectedBg} ${colors.selectedBorder}`
                : 'bg-white/5 border-white/10 hover:border-white/20'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <div className="flex items-center gap-2.5 mb-1">
              <Icon className={`w-5 h-5 ${isSelected ? colors.icon : 'text-white/40'}`} />
              <span className={`text-sm font-semibold ${isSelected ? 'text-white/90' : 'text-white/50'}`}>
                {category.label}
              </span>
            </div>
            {isSelected && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute top-2 right-2 w-5 h-5 rounded-full bg-brand-secondary/30 flex items-center justify-center"
              >
                <span className="text-brand-secondary text-xs font-bold">&#10003;</span>
              </motion.div>
            )}
          </motion.button>
        );
      })}
    </div>
  );
}
